/**
 * Global Assistant API Service
 *
 * SSE streaming client for the unified global assistant endpoint.
 *
 * Flow:
 * 1. Get session token from /api/assistant/session (Vercel backend)
 * 2. Use token to call Railway worker directly for streaming (bypasses Vercel proxy buffering)
 *
 * Note: Vercel rewrites buffer responses, breaking SSE streaming.
 * We call the worker directly for streaming endpoints.
 */

import { getAuthHeaders } from './authConfig';

// Worker URL for direct streaming calls (bypasses Vercel proxy buffering)
// Local dev: worker runs on port 8080
// Production: Railway at service.greenfirefly.ai
const WORKER_URL = import.meta.env.VITE_WORKER_URL ||
  (import.meta.env.DEV ? 'http://localhost:8080' : 'https://service.greenfirefly.ai');

// Session token cache
let cachedSessionToken: string | null = null;
let sessionTokenExpiry: number = 0;

/**
 * Get or refresh the session token for worker authentication.
 * Exported for use by other worker-bound APIs (e.g., playground).
 */
export async function getSessionToken(): Promise<string> {
  // Check if cached token is still valid (with 30s buffer)
  if (cachedSessionToken && Date.now() < sessionTokenExpiry - 30000) {
    return cachedSessionToken;
  }

  const response = await fetch('/api/assistant/session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to get session token: ${response.status}`);
  }

  const data = await response.json();
  cachedSessionToken = data.session_token;
  sessionTokenExpiry = Date.now() + (data.expires_in * 1000);

  return cachedSessionToken;
}

/**
 * Clear the cached session token.
 * Call this when auth state changes (login/logout/team switch) to force a fresh token.
 */
export function clearSessionCache(): void {
  cachedSessionToken = null;
  sessionTokenExpiry = 0;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ValidationError {
  line?: number;
  message: string;
  severity?: string;
}

export interface OpenFileInfo {
  id: string;
  name: string;
  type: string;
  content: string;
  is_focused: boolean;
}

export interface AvailableFileInfo {
  id: string;
  name: string;
  content_type: string;
}

export interface EditorContext {
  file_content?: string;
  file_type?: string;
  validation_errors?: ValidationError[];
  open_files?: OpenFileInfo[];
  available_files?: AvailableFileInfo[];
}

export interface PlaygroundContext {
  agent_content?: string;
  validation_errors?: ValidationError[];
  run_status?: string;
  run_error?: string;
  logs?: string;
  last_output?: string;
  available_files?: AvailableFileInfo[];
}

export interface EvalContext {
  current_eval_id?: string;
  current_run_id?: string;
  eval_functions?: Record<string, unknown>[];
  schedules?: Record<string, unknown>[];
  // Additional context for list/detail pages
  eval_name?: string;
  eval_agent?: string;
  is_optimization?: boolean;
  variants_count?: number;
  runs_count?: number;
  has_running_runs?: boolean;
  eval_count?: number;
  eval_names?: string[];
}

export interface SchemaContext {
  schema_id?: string;
  schema_content?: string;
  validation_errors?: ValidationError[] | string[];
  // Additional context for list/editor pages
  current_schema_id?: string;
  schema_name?: string;
  schema_count?: number;
  schema_names?: string[];
  has_unsaved_changes?: boolean;
}

export interface TrajectoryContext {
  trajectory_id?: string;
}

export type ContextType = 'editor' | 'playground' | 'ide' | 'evals' | 'schemas' | 'trajectories' | 'general';

export interface ExecutionContext {
  mode?: 'oneshot' | 'chat';
  status?: 'idle' | 'running' | 'completed' | 'failed' | 'hitl_pending';
  trajectory_id?: string;
  is_running?: boolean;
}

export interface UnifiedContext {
  context_type: ContextType;
  editor?: EditorContext;
  playground?: PlaygroundContext;
  execution?: ExecutionContext;
  evals?: EvalContext;
  schemas?: SchemaContext;
  trajectories?: TrajectoryContext;
}

export interface ImageData {
  data: string; // base64 encoded
  media_type: string;
}

export interface GlobalAssistantRequest {
  messages: ChatMessage[];
  context?: UnifiedContext;
  model?: string;
  images?: ImageData[];
  conversation_id?: string;
  persist?: boolean;
}

export interface EditorAction {
  action: 'update_content' | 'edit_content' | 'open_file';
  content?: string;
  message?: string;
  item_id?: string;
  name?: string;
  content_type?: string;
  // For edit_content action (search and replace)
  old_text?: string;
  new_text?: string;
}

export interface StreamCallbacks {
  onText?: (content: string) => void;
  onToolStart?: (tool: string, input: Record<string, unknown>) => void;
  onToolResult?: (tool: string, result: Record<string, unknown>) => void;
  onEditorAction?: (action: EditorAction) => void;
  onConversation?: (conversationId: string) => void;
  onError?: (message: string) => void;
  onDone?: () => void;
}

export interface ModelInfo {
  id: string;
  name: string;
  provider: string;
  default?: boolean;
}

export interface ToolInfo {
  name: string;
  description: string;
  category: string;
}

/**
 * Stream a chat request to the global assistant.
 */
export async function streamChat(
  request: GlobalAssistantRequest,
  callbacks: StreamCallbacks,
  signal?: AbortSignal
): Promise<void> {
  // Get session token for worker authentication
  const sessionToken = await getSessionToken();

  // Call worker directly for streaming (Vercel rewrites buffer responses)
  const response = await fetch(`${WORKER_URL}/api/assistant/global/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${sessionToken}`,
    },
    credentials: 'include',
    body: JSON.stringify({
      messages: request.messages,
      context: request.context,
      model: request.model || 'anthropic:claude-sonnet-4-20250514',
      images: request.images,
      conversation_id: request.conversation_id,
      persist: request.persist ?? true,
    }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') {
            callbacks.onDone?.();
            continue;
          }

          try {
            const parsed = JSON.parse(data);

            switch (parsed.type) {
              case 'text':
                callbacks.onText?.(parsed.content);
                break;
              case 'tool_start':
                callbacks.onToolStart?.(parsed.tool, parsed.input);
                break;
              case 'tool_result':
                callbacks.onToolResult?.(parsed.tool, parsed.result);
                break;
              case 'editor_action':
                callbacks.onEditorAction?.({
                  action: parsed.action,
                  content: parsed.content,
                  message: parsed.message,
                  item_id: parsed.item_id,
                  name: parsed.name,
                  content_type: parsed.content_type,
                  old_text: parsed.old_text,
                  new_text: parsed.new_text,
                });
                break;
              case 'conversation':
                callbacks.onConversation?.(parsed.conversation_id);
                break;
              case 'error':
                callbacks.onError?.(parsed.message);
                break;
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Get available models for the global assistant.
 */
export async function getModels(): Promise<ModelInfo[]> {
  const response = await fetch('/api/assistant/global/models', {
    headers: getAuthHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status}`);
  }
  const data = await response.json();
  return data.models;
}

/**
 * Get available tools for the global assistant.
 */
export async function getTools(): Promise<ToolInfo[]> {
  const response = await fetch('/api/assistant/global/tools', {
    headers: getAuthHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status}`);
  }
  const data = await response.json();
  return data.tools;
}
